import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import {
  ACTION_TEMPLATE_OWNER_TYPE_TO_MESSAGE_KEY,
  ActionTemplateOwnerType,
  SubareaItem,
} from 'config/api/actionLibrary/actionLibrary.types';
import action_library_messages from 'messages/action_library_messages';

// Helper function to convert ChoiceItem[] to filter format
const convertChoicesToFilters = (choices: { value: string; label: string }[]) =>
  choices.map(choice => ({ id: choice.value, name: choice.label }));

// Map purpose value to i18n message key for filter display
function getPurposeFilterName(choice: { value: string; label: string }): string[] {
  switch (choice.value) {
    case 'assets':
      return action_library_messages.purpose.assets;
    case 'crisis':
      return action_library_messages.purpose.crisis;
    case 'risks':
      return action_library_messages.purpose.risks;
    default:
      return [`action_library>>purpose>>${choice.value}`, choice.label];
  }
}

// Purpose filter - uses API choices; map value to frontend translation keys
export const getPurposeFilter = async () => {
  const { data } = await actionTemplatesApi.getChoices();
  return {
    sectionName: action_library_messages.filters.purpose,
    filters: (data.purpose || []).map((choice: { value: string; label: string }) => ({
      id: choice.value,
      name: getPurposeFilterName(choice),
    })),
  };
};

// Area filter - uses API choices
export const getAreaFilter = async () => {
  const { data } = await actionTemplatesApi.getChoices();
  return {
    sectionName: action_library_messages.filters.area,
    filters: convertChoicesToFilters(data.area),
  };
};

// Criticality filter - uses API choices
export const getCriticalityFilter = async () => {
  const { data } = await actionTemplatesApi.getChoices();
  return {
    sectionName: action_library_messages.filters.criticality,
    filters: convertChoicesToFilters(data.criticality),
    isSingleSelect: false,
  };
};

// Creator filter - uses API choices (owner_type)
export const getCreatorFilter = async () => {
  const { data } = await actionTemplatesApi.getChoices();
  return {
    sectionName: action_library_messages.filters.creator,
    filters: data.ownerType.map(choice => {
      const messageKey = ACTION_TEMPLATE_OWNER_TYPE_TO_MESSAGE_KEY[choice.value as ActionTemplateOwnerType];
      return {
        id: choice.value,
        name: messageKey ? action_library_messages.owner_type[messageKey] : choice.label,
      };
    }),
  };
};

// Map status value to i18n message key for filter display
function getStatusFilterName(choice: { value: string; label: string }): string[] {
  switch (choice.value) {
    case 'active':
      return action_library_messages.status.active;
    case 'inactive':
      return action_library_messages.status.inactive;
    case 'draft':
      return action_library_messages.status.draft;
    default:
      return [`action_library>>status>>${choice.value}`, choice.label];
  }
}

// Status filter - uses API choices; map value to frontend translation keys (active/inactive)
export const getStatusFilter = async () => {
  const { data } = await actionTemplatesApi.getChoices();
  return {
    sectionName: action_library_messages.filters.status,
    filters: (data.status || []).map((choice: { value: string; label: string }) => ({
      id: choice.value,
      name: getStatusFilterName(choice),
    })),
  };
};

// Subarea filter - dynamic, depends on selected area
// Note: This needs to be handled dynamically in the component based on selected area
export const getSubareaFilter = async (area?: string) => {
  if (!area) {
    return {
      sectionName: action_library_messages.filters.subarea,
      filters: [],
    };
  }

  try {
    const { data } = await actionTemplatesApi.getSubareas(area);
    const subareas: (string | SubareaItem)[] = data.subareas || [];
    return {
      sectionName: action_library_messages.filters.subarea,
      filters: subareas.map((subarea: string | SubareaItem): { id: string; name: string | string[] } => {
        if (typeof subarea === 'string') {
          return { id: subarea, name: subarea };
        }
        const id = String(subarea.value ?? subarea.label);
        const fallback = subarea.label ?? String(subarea.value);
        const name = subarea.name_translation_key ? [subarea.name_translation_key, fallback] : fallback;
        return { id, name };
      }),
    };
  } catch (error) {
    return {
      sectionName: action_library_messages.filters.subarea,
      filters: [],
    };
  }
};

// Legacy exports for backward compatibility (will be replaced with async versions)
export const purposeFilter = {
  sectionName: action_library_messages.filters.purpose,
  filters: [],
};

export const areaFilter = {
  sectionName: action_library_messages.filters.area,
  filters: [],
};

export const criticalityFilter = {
  sectionName: action_library_messages.filters.criticality,
  filters: [],
  isSingleSelect: false,
};

export const creatorFilter = {
  sectionName: action_library_messages.filters.creator,
  filters: [],
};
